import { useEffect, useState, type FormEvent } from "react";
import { useMutation, useQuery } from "@connectrpc/connect-query";
import { Link, useSearchParams } from "react-router-dom";

import {
  getRegistrationOptions,
  registerWithLoginCode,
  requestLoginCode,
} from "@/gen/nodebase/v1/account-AccountService_connectquery";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ErrorNote, Loading } from "@/components/page";
import { sessionFrom, setSession } from "@/lib/session";
import { deviceReturnPath } from "@/routes/device";

export function Register() {
  const [params] = useSearchParams();
  // Set when a device's sign-in link brought the person here. Registering
  // returns to the approval page (App's signed-in /register route reads the
  // same parameter); "Sign in" goes back to it signed out.
  const next = deviceReturnPath(params.get("next"));
  const [step, setStep] = useState<"details" | "code">("details");
  const [invite, setInvite] = useState(params.get("invite") ?? "");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [region, setRegion] = useState("");
  const [error, setError] = useState<unknown>(null);

  const options = useQuery(getRegistrationOptions, {}, { retry: false });
  const requestCode = useMutation(requestLoginCode);
  const register = useMutation(registerWithLoginCode);

  useEffect(() => {
    if (!region && options.data?.defaultWorkerRegion) {
      setRegion(options.data.workerRegions.some((option) => option.id === "us") ? "us" : options.data.defaultWorkerRegion);
    }
  }, [options.data, region]);

  const busy = requestCode.isPending || register.isPending;
  // Once registration is open, an invite is optional and only adds a free
  // Worker; without one, the Worker page offers a subscription.
  const inviteRequired = options.data?.inviteRequired ?? true;
  const freeWorker = inviteRequired || invite.trim() !== "";

  async function sendCode(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      await requestCode.mutateAsync({ email: email.trim() });
      setStep("code");
    } catch (err) {
      setError(err);
    }
  }

  async function complete(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      const res = await register.mutateAsync({
        email: email.trim(),
        code: code.trim(),
        inviteCode: invite.trim(),
        workerRegion: region,
      });
      setSession(sessionFrom(email.trim(), res.accountId, res.session, true));
    } catch (err) {
      setError(err);
    }
  }

  return (
    <div className="flex min-h-dvh items-center justify-center bg-background px-4">
      <Card className="w-full max-w-md" data-testid="register-card">
        <CardHeader>
          <CardTitle>Create your Nodebase account</CardTitle>
          <CardDescription>
            {inviteRequired
              ? "Use your invite to create a private Worker — a cloud computer for your projects and tasks. Then connect using Desktop and work with your AI."
              : "Create your account, then start a private Worker — a cloud computer for your projects and tasks. Connect using Desktop and work with your AI."}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {options.isPending ? (
            <Loading />
          ) : (
            <form className="space-y-4" onSubmit={step === "details" ? sendCode : complete}>
              <ErrorNote error={error ?? options.error} />
              {options.isError && (
                <Button type="button" variant="outline" disabled={options.isFetching} onClick={() => void options.refetch()}>
                  Retry loading locations
                </Button>
              )}
              <div className="space-y-1.5">
                <Label htmlFor="invite">{inviteRequired ? "Invite code" : "Invite code (optional)"}</Label>
                <Input
                  id="invite"
                  name="invite"
                  required={inviteRequired}
                  autoFocus={inviteRequired && !invite}
                  value={invite}
                  onChange={(e) => setInvite(e.target.value)}
                  disabled={step === "code" || busy}
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="register-email">Email</Label>
                <Input
                  id="register-email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  autoFocus={Boolean(invite) || !inviteRequired}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={step === "code" || busy}
                />
              </div>
              <fieldset className="space-y-2" disabled={step === "code" || busy}>
                <legend className="text-sm font-medium">Worker location</legend>
                <div className="grid grid-cols-2 gap-2">
                  {(options.data?.workerRegions ?? []).map((option) => (
                    <label
                      key={option.id}
                      className={`cursor-pointer rounded-md border p-3 text-sm ${region === option.id ? "border-primary bg-secondary" : ""}`}
                    >
                      <input
                        className="mr-2"
                        type="radio"
                        name="worker-region"
                        value={option.id}
                        checked={region === option.id}
                        onChange={() => setRegion(option.id)}
                      />
                      {option.displayName}
                    </label>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  Choose a location near you for a faster connection. Your projects
                  and data live there; moving the Worker later requires a migration.
                </p>
              </fieldset>
              {step === "details" && (
                <Button type="button" variant="ghost" className="w-full" disabled={busy || options.isError || !region}
                  onClick={(e) => {
                    if (!e.currentTarget.form?.reportValidity()) return;
                    setError(null);
                    setStep("code");
                  }}>
                  I already have a code
                </Button>
              )}
              {step === "code" && (
                <div className="space-y-1.5">
                  <Label htmlFor="register-code">Email verification code</Label>
                  <Input
                    id="register-code"
                    name="code"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    required
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                  />
                  <p className="text-xs text-muted-foreground">
                    Sent to {email.trim()}.{invite.trim() && " Your invite is consumed only when registration succeeds."}
                  </p>
                </div>
              )}
              <Button type="submit" className="w-full" disabled={busy || options.isError || !options.data?.workerRegions.some((option) => option.id === region)}>
                {step === "details" ? "Send verification code" : freeWorker ? "Create account & start Worker" : "Create account"}
              </Button>
              {step === "code" && (
                <Button type="button" variant="ghost" className="w-full" disabled={busy} onClick={() => { setCode(""); setError(null); setStep("details"); }}>
                  Change details
                </Button>
              )}
              <p className="text-center text-sm text-muted-foreground">
                Already registered?{" "}
                <Link className="underline underline-offset-4" to={next || "/login"}>Sign in</Link>
              </p>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
